import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KeyboardAvoidingView, Modal, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { GradientButton } from '../components/GradientButton';
import { Button } from '../components/Button';
import { ScreenHeader } from '../components/ScreenHeader';
import { FondAube } from '../components/FondAube';
import { BilanModifications } from '../components/BilanModifications';
import { OptimisationEnCours } from '../components/OptimisationEnCours';
import { PiedAction } from '../components/PiedAction';
import { CarteModification } from '../components/CarteModification';
import { useClaude } from '../hooks/useClaude';
import { useGabarit } from '../hooks/useGabarit';
import { saveGeneratedCv } from '../services/cv';
import { lireDocumentImporte } from '../services/mediaPicker';
import type { CvImproveInput, CvOfferInput } from '../services/claude';
import type { CvChanges } from '../types';
import { appliquerModifications, lieuModification, nombreAppliquees } from '../utils/appliquerModifications';
import { DEFAULT_ACCENT, DEFAULT_TEMPLATE } from '../theme/templates';
import { colors, layout, radius, screenPadding, spacing, typography } from '../theme/tokens';
import type { CvCreateInitial } from './CvCreateScreen';
import type { CvImproveSource } from './CvScoreScreen';
import { t, tBrut } from '../i18n';
import { FicheDePosteChamp } from '../components/FicheDePosteChamp';
import { useFicheDePoste } from '../hooks/useFicheDePoste';
import { offresActives } from '../config/offres';

export interface CvImproveScreenProps {
  cv: CvImproveSource;
  onBack?: () => void;
  // Signale à la couche navigation qu'une saisie en cours serait perdue (garde de sortie).
  onDirtyChange?: (dirty: boolean) => void;
  onDone?: (initial: CvCreateInitial) => void;
}

/**
 * « Optimiser mon CV » (1.2.0) : un seul appel IA rend des modifications précises, que la personne
 * garde ou écarte une à une avant de les appliquer.
 * choix (pop-up) → [fiche : page dédiée] → loading → revue → applying ; vide et blocked à part.
 * `vide` : aucune modification utile, et rien n'est décompté.
 */
type Phase = 'choix' | 'fiche' | 'loading' | 'revue' | 'applying' | 'vide' | 'blocked';

/**
 * Rend la charge utile OU un message — jamais `null` nu, qui ne disait pas POURQUOI.
 *
 * L'ancienne signature `Promise<CvImproveInput | null>` confondait deux absences très différentes :
 * « il n'y a pas de source » et « la source n'a pas pu être lue ». La seconde arrivait ici sous la
 * forme d'une exception, qui n'était pas rattrapée : l'effet de montage s'interrompait avant tout
 * `setPhase` et l'écran restait figé sur son chargement.
 */
async function buildPayload(
  cv: CvImproveSource,
): Promise<{ payload?: CvImproveInput; error?: string }> {
  if (cv.file) return lireDocumentImporte(cv.file);
  if (cv.text && cv.text.trim()) return { payload: { text: cv.text } };
  return {};
}

export function CvImproveScreen({ cv, onBack, onDone, onDirtyChange }: CvImproveScreenProps) {
  const { improveCvChanges, error } = useClaude();
  const { large } = useGabarit();
  const sansSource = !cv.file && !(cv.text && cv.text.trim());
  const [phase, setPhase] = useState<Phase>(sansSource ? 'blocked' : 'choix');
  const [resultat, setResultat] = useState<CvChanges | null>(null);
  // Modifications gardées : toutes au départ, la personne écarte ce qui ne lui convient pas.
  const [retenues, setRetenues] = useState<ReadonlySet<string>>(new Set());
  const [localError, setLocalError] = useState<string | null>(sansSource ? t('cvImprove.notFound') : null);
  // Parcours terminé : sans ce drapeau, la phase resterait 'applying' après succès et un retour
  // ultérieur sur cet écran déclencherait une alerte pour un flux pourtant abouti.
  const [finished, setFinished] = useState(false);
  // Fiche de poste facultative, sources cumulées. Préremplie quand une notation l'a déjà utilisée.
  const ficheDeLaNotation = !!cv.fiche && !!(cv.fiche.text?.trim() || cv.fiche.file || cv.fiche.images?.length);
  const fiche = useFicheDePoste({ onErreur: setLocalError, cumul: true, initiale: cv.fiche });
  // Construite (et donc vérifiée : lecture, pages, poids) AVANT l'appel, réutilisée telle quelle :
  // une fiche illisible se signale sur sa page, pas après l'attente.
  const ficheConstruite = useRef<CvOfferInput | undefined>(undefined);
  const monte = useRef(true);
  // Garde SYNCHRONE contre le double appui : `disabled` ne prend effet qu'au rendu suivant, et deux
  // appuis rapprochés enregistreraient deux CV.
  const enregistrement = useRef(false);
  useEffect(
    () => () => {
      monte.current = false;
    },
    [],
  );

  const lancer = useCallback(async () => {
    setLocalError(null);
    setPhase('loading');
    const { payload, error: erreurLecture } = await buildPayload(cv);
    if (!monte.current) return;
    if (!payload) {
      // Une lecture ratée porte son propre message ; sans source du tout, celui d'avant.
      setLocalError(erreurLecture ?? t('cvImprove.notFound'));
      setPhase('blocked');
      return;
    }
    const offer = ficheConstruite.current;
    // Sans fiche, l'appel à un seul argument.
    const result = offer ? await improveCvChanges(payload, offer) : await improveCvChanges(payload);
    if (!monte.current) return;
    if (!result) {
      // null = quota (Paywall déjà ouvert par useClaude) ou erreur réseau.
      setPhase('blocked');
      return;
    }
    if (!result.cv || !Array.isArray(result.changes)) {
      // Réponse incomplète (serveur pas à jour, coupure) : un message, jamais un écran figé.
      setLocalError(t('cvImprove.reponseInvalide'));
      setPhase('blocked');
      return;
    }
    if (result.changes.length === 0) {
      setPhase('vide');
      return;
    }
    setResultat(result);
    setRetenues(new Set(result.changes.map((c) => c.id)));
    setPhase('revue');
  }, [cv, improveCvChanges]);

  const sansFiche = () => {
    fiche.vider();
    ficheConstruite.current = undefined;
    void lancer();
  };

  const optimiserAvecFiche = async () => {
    setLocalError(null);
    try {
      ficheConstruite.current = await fiche.construire();
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : t('cvScore.failed'));
      return;
    }
    void lancer();
  };

  // Les propositions ont déjà consommé un cv_improve : les quitter sans les appliquer les perd.
  const dirty = !finished && (phase === 'revue' || phase === 'applying' || (phase === 'fiche' && fiche.presente));
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const total = resultat?.changes.length ?? 0;
  const appliquees = useMemo(
    () => (resultat ? nombreAppliquees(resultat.cv, resultat.changes, retenues) : 0),
    [resultat, retenues],
  );

  const basculer = (id: string, retenue: boolean) =>
    setRetenues((prec) => {
      const suivant = new Set(prec);
      if (retenue) suivant.add(id);
      else suivant.delete(id);
      return suivant;
    });

  const appliquer = async () => {
    if (!resultat || appliquees === 0 || enregistrement.current) return;
    enregistrement.current = true;
    setLocalError(null);
    setPhase('applying');
    const optimized = appliquerModifications(resultat.cv, resultat.changes, retenues);
    try {
      const title = optimized.headline?.trim() || tBrut('cvImprove.defaultTitle');
      const cvId = await saveGeneratedCv(optimized, title);
      setFinished(true);
      onDone?.({
        cvId,
        cv: optimized,
        title,
        template: DEFAULT_TEMPLATE,
        accentColor: DEFAULT_ACCENT,
        ficheDePoste: !!ficheConstruite.current,
      });
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : t('cvImprove.saveFailed'));
      enregistrement.current = false;
      setPhase('revue');
    }
  };

  const shownError = localError ?? error;
  const enRevue = phase === 'revue' || phase === 'applying';

  const cartes = resultat
    ? resultat.changes.map((c) => (
        <CarteModification
          key={c.id}
          testID={`cvimprove-modif-${c.id}`}
          change={c}
          lieu={lieuModification(resultat.cv, c)}
          retenue={retenues.has(c.id)}
          onToggle={(retenue) => basculer(c.id, retenue)}
          disabled={phase === 'applying'}
        />
      ))
    : null;

  const contenuPied = (
    <>
      {/* Dans le pied, à côté du bouton : après huit cartes, une erreur en bas de liste resterait hors de vue. */}
      {shownError ? (
        <Text testID="cvimprove-error" accessibilityLiveRegion="polite" style={styles.error}>
          {shownError}
        </Text>
      ) : null}
      <Text testID="cvimprove-retenues" style={styles.statut}>
        {t('cvImprove.pied.retenues', { count: appliquees, total })}
      </Text>
      <GradientButton
        variante="cta"
        hauteur="lg"
        label={appliquees > 0 ? t('cvImprove.pied.appliquer', { count: appliquees }) : t('cvImprove.pied.aucune')}
        onPress={() => void appliquer()}
        disabled={appliquees === 0}
        loading={phase === 'applying'}
        testID="cvimprove-appliquer"
      />
    </>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <FondAube />
      {/* Depuis la page « Fiche de poste », le retour ramène à la pop-up, CV gardé. */}
      <ScreenHeader
        variante="carte"
        title={t('cvImprove.title')}
        onBack={phase === 'fiche' ? () => setPhase('choix') : onBack}
        testID="cvimprove-back"
      />

      <Modal visible={phase === 'choix'} transparent animationType="fade" onRequestClose={() => onBack?.()}>
        <View style={styles.voile}>
          <View style={styles.popup} testID="cvimprove-choix">
            <Text style={styles.popupTexte}>{t(ficheDeLaNotation ? 'cvImprove.choixDeja' : 'cvImprove.choixQuestion')}</Text>
            {ficheDeLaNotation ? (
              <GradientButton label={t('cvImprove.choixUtiliser')} onPress={() => setPhase('fiche')} testID="cvimprove-choix-utiliser" />
            ) : (
              <GradientButton label={t('cvImprove.choixOui')} onPress={() => setPhase('fiche')} testID="cvimprove-choix-oui" />
            )}
            <Button title={t('cvImprove.choixNon')} variant="secondary" onPress={sansFiche} testID="cvimprove-choix-non" />
          </View>
        </View>
      </Modal>

      {large && enRevue && resultat ? (
        <View style={styles.revueL} testID="cvimprove-revue-colonnes">
          <View style={styles.revueGaucheL}>
            <BilanModifications changes={resultat.changes} />
            <PiedAction testID="cvimprove-pied" enColonne>
              {contenuPied}
            </PiedAction>
          </View>
          <ScrollView style={styles.flex} contentContainerStyle={styles.revueCartesL} showsVerticalScrollIndicator={false}>
            {cartes}
          </ScrollView>
        </View>
      ) : (
        <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
          <ScrollView contentContainerStyle={[styles.content, large && styles.contentL]} keyboardShouldPersistTaps="handled">
            {/* L'attente et la revue ont leur propre en-tête : la phrase d'aide n'y serait qu'un doublon. */}
            {phase === 'loading' || enRevue ? null : (
              <Text style={styles.headerSubtitle}>{t('cvImprove.subtitle')}</Text>
            )}

            {phase === 'fiche' ? (
              <>
                <View style={styles.ficheEntete}>
                  <Text style={styles.ficheTitre} accessibilityRole="header">
                    {t('cvImprove.ficheTitre')}
                  </Text>
                  <Text style={styles.headerSubtitle}>{t('cvImprove.ficheSousTitre')}</Text>
                </View>
                <FicheDePosteChamp
                  fiche={fiche}
                  prefixe="cvimprove-offer"
                  titre={t('cvImprove.ficheChamp')}
                  aide={t('cvImprove.ficheAide')}
                  placeholder={t('cvScore.offerPlaceholder')}
                  libelleFichier={t('cvScore.offerFile')}
                  libelleCaptures={t('cvScore.offerCaptures')}
                  avecOffresGardees={offresActives()}
                  cumul
                />
                {shownError ? (
                  <Text testID="cvimprove-error" style={styles.error}>
                    {shownError}
                  </Text>
                ) : null}
                <GradientButton
                  label={t('cvImprove.optimiser')}
                  onPress={() => void optimiserAvecFiche()}
                  disabled={!fiche.presente}
                  withArrow
                  testID="cvimprove-optimiser"
                />
                <Button title={t('cvImprove.passer')} variant="secondary" onPress={sansFiche} testID="cvimprove-passer" />
              </>
            ) : null}

            {phase === 'loading' ? (
              <View testID="cvimprove-loading">
                <OptimisationEnCours testID="cvimprove-progress" />
              </View>
            ) : null}

            {phase === 'blocked' ? (
              <Text testID="cvimprove-blocked" style={styles.muted}>
                {shownError ?? t('cvImprove.blocked')}
              </Text>
            ) : null}

            {phase === 'vide' ? (
              <View testID="cvimprove-vide" style={styles.vide}>
                <Text accessibilityRole="header" style={styles.videTitre}>
                  {t('cvImprove.vide.titre')}
                </Text>
                <Text style={styles.headerSubtitle}>{t('cvImprove.vide.detail')}</Text>
                <Button title={t('common.back')} variant="secondary" onPress={() => onBack?.()} testID="cvimprove-vide-retour" />
              </View>
            ) : null}

            {enRevue && resultat ? (
              <>
                <BilanModifications changes={resultat.changes} />
                {cartes}
              </>
            ) : null}
          </ScrollView>
        </KeyboardAvoidingView>
      )}

      {enRevue && resultat && !large ? (
        <PiedAction testID="cvimprove-pied">{contenuPied}</PiedAction>
      ) : null}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  contentL: { width: '100%', maxWidth: 760, alignSelf: 'center', padding: spacing.xl },
  revueL: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.xl,
    width: '100%',
    maxWidth: layout.pageMaxWidth,
    alignSelf: 'center',
    padding: spacing.xl,
  },
  revueGaucheL: { width: 360, gap: spacing.lg },
  revueCartesL: { gap: spacing.md, paddingBottom: spacing.xl },
  container: { flex: 1, backgroundColor: colors.grey50 },
  flex: { flex: 1 },
  content: { padding: screenPadding, gap: spacing.md },

  headerSubtitle: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },

  muted: { fontSize: typography.body.size, color: colors.grey500, textAlign: 'center' },
  error: { color: colors.error, fontSize: typography.small.size, textAlign: 'center' },

  voile: { flex: 1, backgroundColor: colors.voile, justifyContent: 'center', padding: screenPadding },
  popup: { gap: spacing.md, padding: spacing.xl, borderRadius: radius.xl, backgroundColor: colors.white },
  popupTexte: { fontSize: typography.h3.size, lineHeight: typography.h3.lineHeight, fontWeight: typography.h3.weight, color: colors.night },
  ficheEntete: { gap: spacing.xs },
  ficheTitre: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  note: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },

  vide: { gap: spacing.md, marginTop: spacing.lg },
  videTitre: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  statut: { fontSize: typography.small.size, fontWeight: '700', color: colors.grey500, textAlign: 'center' },
});
